/**
 * API Client - Centralized HTTP client for NetEasyFTTX
 */

import { authService } from './authService'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://neteasy.wispchat.net'
const DEFAULT_TENANT = import.meta.env.VITE_DEFAULT_TENANT || 'demo-isp'

export interface ApiOptions {
  tenantSlug?: string
  headers?: Record<string, string>
}

class ApiClient {
  public readonly baseUrl: string
  private defaultTenant: string

  constructor(baseUrl: string, defaultTenant: string) {
    this.baseUrl = baseUrl
    this.defaultTenant = defaultTenant
  }

  private getHeaders(options?: ApiOptions): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options?.headers,
    }

    const token = authService.getToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const user = authService.getUser()
    const slug = options?.tenantSlug || user?.tenant?.slug || this.defaultTenant
    if (slug) {
      headers['X-Tenant-Slug'] = slug
    }

    if (user?.role === 'SUPER_ADMIN') {
      const imp = authService.getImpersonateSlug()
      if (imp) {
        headers['X-Impersonate-Tenant-Slug'] = imp
      }
    }

    return headers
  }

  async get<T>(endpoint: string, options?: ApiOptions): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders(options),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.error || `API Error: ${response.status}`)
    }

    return response.json()
  }

  async post<T>(endpoint: string, data: unknown, options?: ApiOptions): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(options),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.error || `API Error: ${response.status}`)
    }

    return response.json()
  }

  async put<T>(endpoint: string, data: unknown, options?: ApiOptions): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers: this.getHeaders(options),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.error || `API Error: ${response.status}`)
    }

    return response.json()
  }

  async patch<T>(endpoint: string, data: unknown, options?: ApiOptions): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PATCH',
      headers: this.getHeaders(options),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.error || `API Error: ${response.status}`)
    }

    return response.json()
  }

  async delete<T>(endpoint: string, options?: ApiOptions): Promise<T | void> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders(options),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.error || `API Error: ${response.status}`)
    }

    if (response.status === 204) {
      return undefined as unknown as T
    }

    return response.json()
  }

  async healthCheck(): Promise<{ status: string; timestamp: string; database: string }> {
    return this.get('/health')
  }

  getTenantSlug(): string {
    return this.defaultTenant
  }

  setTenantSlug(slug: string): void {
    this.defaultTenant = slug
  }
}

export const apiClient = new ApiClient(API_BASE_URL, DEFAULT_TENANT)

export { API_BASE_URL }
